import AsyncStorage from '@react-native-async-storage/async-storage';

export type ChatGif = {
  id: string;
  title: string;
  url: string;
  previewUrl: string;
  width: number;
  height: number;
  provider: 'giphy';
};

const GIPHY_API_KEY = 'lngysKtSODy90AVl5i0nnlTJJw0Wq0wq';
const GIPHY_BASE_URL = 'https://api.giphy.com/v1/gifs';
const RECENT_GIFS_KEY = '@tonyte/chat/recent-gifs';

function toNumber(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function normalizeGif(item: any): ChatGif | null {
  const original = item?.images?.original;
  const preview = item?.images?.fixed_width || item?.images?.downsized_medium || original;
  const url = original?.url || item?.url;
  const previewUrl = preview?.webp || preview?.url || url;
  if (!item?.id || !url || !previewUrl) return null;

  return {
    id: String(item.id),
    title: String(item.title || 'GIF').trim() || 'GIF',
    url: String(url),
    previewUrl: String(previewUrl),
    width: toNumber(original?.width || preview?.width, 320),
    height: toNumber(original?.height || preview?.height, 240),
    provider: 'giphy',
  };
}

async function requestGiphy(path: 'trending' | 'search', params: Record<string, string | number>): Promise<ChatGif[]> {
  const query = new URLSearchParams({
    api_key: GIPHY_API_KEY,
    rating: 'pg-13',
    bundle: 'messaging_non_clips',
    ...Object.fromEntries(Object.entries(params).map(([key, value]) => [key, String(value)])),
  });
  const response = await fetch(`${GIPHY_BASE_URL}/${path}?${query.toString()}`);
  if (!response.ok) throw new Error(`GIF provider failed (${response.status})`);
  const payload = await response.json();
  return Array.isArray(payload?.data) ? payload.data.map(normalizeGif).filter(Boolean) : [];
}

export async function getTrendingGifs(limit = 30): Promise<ChatGif[]> {
  return requestGiphy('trending', { limit });
}

export async function searchGifs(query: string, limit = 30): Promise<ChatGif[]> {
  const q = query.trim();
  if (!q) return getTrendingGifs(limit);
  return requestGiphy('search', { q, limit });
}

export async function getRecentGifs(): Promise<ChatGif[]> {
  try {
    const raw = await AsyncStorage.getItem(RECENT_GIFS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(item => item?.id && item?.url && item?.previewUrl).slice(0, 12) : [];
  } catch {
    return [];
  }
}

export async function saveRecentGif(gif: ChatGif): Promise<void> {
  try {
    const current = await getRecentGifs();
    const next = [gif, ...current.filter(item => item.id !== gif.id)].slice(0, 12);
    await AsyncStorage.setItem(RECENT_GIFS_KEY, JSON.stringify(next));
  } catch {
    // Recently used GIFs are a convenience cache; sending should never depend on it.
  }
}
